import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, ArrowRight, ArrowUpRight, MapPin } from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import Newsletter from "../components/site/home/Newsletter";
import MonetizationBlocks from "../components/site/monetization/MonetizationBlocks";
import Breadcrumbs from "../components/site/Breadcrumbs";
import { useSeo } from "../hooks/useSeo";
import { THEME_PAGES } from "../data/sitePages";
import { DESTINATIONS } from "../data/destinations";
import { photoFromPool } from "../data/themePhotos";
import { faqPage, breadcrumb } from "../lib/jsonld";
import { SECTIONS } from "../data/longContent";

const List = ({ title, items, testid }) => (
  <div data-testid={testid}>
    <h2 className="font-display text-2xl text-brand-ink md:text-3xl">{title}</h2>
    <ul className="mt-5 space-y-3">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-3 text-brand-inkSoft">
          <Check size={18} className="mt-0.5 shrink-0 text-brand-terracotta" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function ThemeLanding({ slug }) {
  const navigate = useNavigate();
  const page = THEME_PAGES[slug];

  useSeo({
    title: page.title,
    description: page.description,
    path: `/${page.slug}`,
    image: photoFromPool(page.theme, 0),
    jsonLd: [
      faqPage(page.faqs),
      breadcrumb([
        { name: "Home", path: "/" },
        { name: page.h1, path: `/${page.slug}` },
      ]),
    ],
  });

  const destinations = page.destinationIds
    .map((id) => DESTINATIONS.find((d) => d.id === id))
    .filter(Boolean);

  return (
    <div data-testid={`theme-page-${slug}`} className="bg-brand-bg text-brand-ink">
      <Header onNavigate={() => navigate("/")} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={photoFromPool(page.theme, 0)} alt={page.h1} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-black/45" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1100px] px-6 pt-40 pb-20 md:px-10 md:pt-48 md:pb-28">
          <p className="overline text-white/80">{page.eyebrow}</p>
          <h1 className="hero-h1 mt-4 max-w-3xl text-4xl text-white md:text-6xl">{page.h1}</h1>
          <p className="mt-6 max-w-2xl text-base text-white/85 md:text-lg">{page.intro[0]}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/find-my-retreat" data-testid="theme-cta-primary" className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-medium text-brand-ink transition-colors hover:bg-brand-bg2">Find My Retreat <ArrowRight size={15} /></Link>
            <a href="/#escapes" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-medium text-white backdrop-blur-md transition-colors hover:bg-white/10">Explore all retreats</a>
          </div>
        </div>
      </section>

      {/* Intro + expect/who */}
      <section className="mx-auto max-w-[1100px] px-6 py-20 md:px-10 md:py-24">
        <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: page.h1, path: `/${page.slug}` }]} />
        <div className="mt-8">
        {page.intro.slice(1).map((p, i) => (
          <p key={i} className="mb-5 max-w-3xl text-lg text-brand-inkSoft">{p}</p>
        ))}
        </div>
        <div className="mt-12 grid grid-cols-1 gap-12 md:grid-cols-2">
          <List title="What to expect" items={page.expect} testid="theme-expect" />
          <List title="Who it's for" items={page.whoFor} testid="theme-whofor" />
        </div>

        {(SECTIONS[slug] || []).map((sec, i) => (
          <div key={i} data-testid={`theme-section-${i}`} className="mt-14 max-w-3xl">
            <h2 className="font-display text-2xl text-brand-ink md:text-3xl">{sec.h2}</h2>
            {sec.paras.map((p, j) => (
              <p key={j} className="mt-4 text-lg text-brand-inkSoft">{p}</p>
            ))}
          </div>
        ))}

        <div className="mt-14">
          <a href="/find-my-retreat" data-testid="theme-inline-cta" className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-7 py-3.5 text-sm font-medium text-white transition-colors hover:bg-brand-inkSoft">
            Not sure which suits you? Take the Find My Retreat quiz <ArrowRight size={15} />
          </a>
        </div>
      </section>

      {/* Popular real destinations */}
      <section className="bg-brand-bg2">
        <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-24">
          <h2 className="font-display text-2xl text-brand-ink md:text-4xl">Popular destinations</h2>
          <p className="mt-2 text-brand-inkSoft">Real curated escapes from our catalogue that suit this style.</p>
          <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            {destinations.map((d, i) => (
              <Link key={d.id} to={`/escape/${d.id}`} data-testid={`theme-dest-${d.id}`} className="group block">
                <div className="card-img-wrap img-gradient-overlay relative aspect-[4/3] w-full bg-brand-bg2">
                  <img src={d.image} alt={`${d.title}, ${d.country}`} loading="lazy" className="card-img absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute inset-x-0 bottom-0 z-10 p-5">
                    <h3 className="font-display text-xl leading-tight text-white">{d.title}</h3>
                    <p className="mt-1 inline-flex items-center gap-1 text-sm text-white/80"><MapPin size={13} /> {d.country}</p>
                  </div>
                </div>
                <p className="mt-3 flex items-center gap-1.5 px-1 text-sm font-medium text-brand-ink">Explore {d.title} <ArrowUpRight size={15} className="text-brand-terracotta transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-[900px] px-6 py-20 md:px-10 md:py-24">
        <h2 className="font-display text-2xl text-brand-ink md:text-4xl">Frequently asked questions</h2>
        <div className="mt-8 divide-y divide-brand-line border-y border-brand-line">
          {page.faqs.map((f, i) => (
            <div key={i} data-testid={`theme-faq-${i}`} className="py-6">
              <h3 className="font-display text-lg text-brand-ink">{f.q}</h3>
              <p className="mt-2 text-brand-inkSoft">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Related themes */}
      <section className="mx-auto max-w-[1100px] px-6 pb-8 md:px-10">
        <p className="overline text-brand-inkSoft">Related retreat styles</p>
        <div className="mt-4 flex flex-wrap gap-3">
          {page.related.map((rs) => (
            <Link key={rs} to={`/${rs}`} data-testid={`theme-related-${rs}`} className="rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm font-medium text-brand-ink transition-colors hover:bg-brand-ink hover:text-white">
              {THEME_PAGES[rs].h1}
            </Link>
          ))}
        </div>
      </section>

      <MonetizationBlocks browseTo="/#escapes" recommendationStyle={page.h1} />
      <Newsletter />
      <Footer />
    </div>
  );
}